import React, { useState } from "react";
import { useMutation } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import IconButton from "@material-ui/core/IconButton";

import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import VisibilityIcon from "@material-ui/icons/Visibility";
import LockIcon from "@material-ui/icons/Lock";
import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";

import { login } from "utils/auth";
import PageHeader from "designV2/components/PageHeader";
import Panel from "designV2/components/Panel";
import Field from "designV2/components/Field";
import Button from "designV2/components/Button";
import "./changePassword.css";

/* ------------------------------------------------------------------ *
 * GraphQL
 *
 * changeShopAdminPassword returns a fresh access/refresh token pair —
 * changing the password invalidates the session's current access
 * token server-side (it's keyed by a token id stored in Redis, which
 * this mutation overwrites), so the pre-redesign screen was actually
 * breaking the user's own session: it discarded the returned token
 * and kept using the now-invalid one until an API call happened to
 * fail and the app's error link silently recovered. Adopting the new
 * token here avoids that trip through a broken state entirely.
 * ------------------------------------------------------------------ */

const CHANGE_PASSWORD = gql`
  mutation changeShopAdminPassword($newPassword: String!, $currentPassword: String!) {
    changeShopAdminPassword(input: { newPassword: $newPassword, currentPassword: $currentPassword }) {
      _id
      token {
        accessToken
        refreshToken
      }
    }
  }
`;

const readableError = (error) => {
  if (!error) return "";
  if (error.graphQLErrors && error.graphQLErrors.length > 0) {
    return error.graphQLErrors[0].message;
  }
  return error.message || "Something went wrong, please try again.";
};

// Advisory only — the backend only actually requires 8+ characters
// (see changeShopAdminPasswordValidation on the backend). This never
// blocks submission; it just encourages a stronger password.
const getStrength = (password) => {
  if (!password) return { score: 0, label: "" };
  let score = 0;
  if (password.length >= 8) score += 1;
  if (password.length >= 12) score += 1;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score += 1;
  if (/\d/.test(password)) score += 1;
  if (/[^A-Za-z0-9]/.test(password)) score += 1;
  const labels = ["Very weak", "Weak", "Fair", "Good", "Strong", "Very strong"];
  const tones = ["danger", "danger", "warning", "warning", "success", "success"];
  return { score, label: labels[score], tone: tones[score] };
};

const PasswordField = ({ label, value, onChange, visible, onToggleVisible, error, hint, autoComplete }) => (
  <Field label={label} required error={error} hint={hint}>
    <div className="changePasswordInputRow">
      <input
        type={visible ? "text" : "password"}
        className="dsInput"
        value={value}
        onChange={onChange}
        autoComplete={autoComplete}
      />
      <IconButton size="small" className="changePasswordVisibilityToggle" onClick={onToggleVisible} tabIndex={-1}>
        {visible ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
      </IconButton>
    </div>
  </Field>
);

const ChangePassword = () => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [currentVisible, setCurrentVisible] = useState(false);
  const [newVisible, setNewVisible] = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);

  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [success, setSuccess] = useState(false);

  const [changePassword, { loading: submitting }] = useMutation(CHANGE_PASSWORD);

  const strength = getStrength(newPassword);
  const meetsLength = newPassword.length >= 8;
  const matches = Boolean(confirmPassword) && confirmPassword === newPassword;

  const resetForm = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setFieldErrors({});
    setFormError("");
  };

  const validate = () => {
    const errors = {};
    if (!currentPassword) errors.currentPassword = "Enter your current password.";
    if (!newPassword) {
      errors.newPassword = "Enter a new password.";
    } else if (newPassword.length < 8) {
      errors.newPassword = "Use at least 8 characters.";
    } else if (currentPassword && newPassword === currentPassword) {
      errors.newPassword = "Choose a password you haven't used before.";
    }
    if (!confirmPassword) {
      errors.confirmPassword = "Confirm your new password.";
    } else if (confirmPassword !== newPassword) {
      errors.confirmPassword = "Doesn't match the new password.";
    }
    return errors;
  };

  const handleSubmit = () => {
    if (submitting) return;
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setFormError("");
      return;
    }
    setFieldErrors({});
    setFormError("");

    changePassword({
      variables: {
        newPassword: newPassword.trim(),
        currentPassword: currentPassword.trim(),
      },
    })
      .then((res) => {
        // Keeps the login page's "remember me" pre-fill (see layouts/shopAdmin/Login.js)
        // in sync so it doesn't offer the password that was just replaced.
        window.localStorage.setItem("password", btoa(newPassword.trim()));
        setSuccess(true);
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");

        const { accessToken, refreshToken } = res.data.changeShopAdminPassword.token;
        // A short pause so "Password changed" is actually visible before the
        // session refresh (see the note above the mutation) reloads the app.
        window.setTimeout(() => {
          login(accessToken, refreshToken, "refreshToken");
        }, 1600);
      })
      .catch((err) => setFormError(readableError(err)));
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" || e.charCode === 13) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="page changePasswordPage">
      <main className="main">
        <PageHeader title="Change password" description="Update the password you use to sign in to this dashboard." />

        <Panel>
          <div className="changePasswordForm" onKeyPress={handleKeyPress}>
            {formError && (
              <div className="dsFormMessage error" role="alert">
                {formError}
              </div>
            )}
            {success && (
              <div className="dsFormMessage success" role="status">
                <CheckIcon fontSize="small" /> Password changed. Refreshing your session...
              </div>
            )}

            <PasswordField
              label="Current password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              visible={currentVisible}
              onToggleVisible={() => setCurrentVisible((v) => !v)}
              error={fieldErrors.currentPassword}
              autoComplete="current-password"
            />

            <PasswordField
              label="New password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              visible={newVisible}
              onToggleVisible={() => setNewVisible((v) => !v)}
              error={fieldErrors.newPassword}
              autoComplete="new-password"
            />

            {newPassword && (
              <div className="changePasswordStrength">
                <div className="changePasswordStrengthBar">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <span
                      key={i}
                      className={`changePasswordStrengthSegment ${
                        i < strength.score ? `tone-${strength.tone}` : ""
                      }`}
                    />
                  ))}
                </div>
                <span className={`changePasswordStrengthLabel tone-${strength.tone}`}>{strength.label}</span>
              </div>
            )}

            <div className={`changePasswordRequirement ${meetsLength ? "isMet" : ""}`}>
              {meetsLength ? <CheckIcon fontSize="small" /> : <CloseIcon fontSize="small" />}
              At least 8 characters
            </div>

            <PasswordField
              label="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              visible={confirmVisible}
              onToggleVisible={() => setConfirmVisible((v) => !v)}
              error={fieldErrors.confirmPassword}
              autoComplete="new-password"
            />

            {confirmPassword && (
              <div className={`changePasswordRequirement ${matches ? "isMet" : ""}`}>
                {matches ? <CheckIcon fontSize="small" /> : <CloseIcon fontSize="small" />}
                {matches ? "Passwords match" : "Doesn't match the new password yet"}
              </div>
            )}

            <div className="changePasswordActions">
              <Button onClick={resetForm} type="button">
                Reset
              </Button>
              <Button variant="primary" icon={<LockIcon fontSize="small" />} onClick={handleSubmit} loading={submitting}>
                Change password
              </Button>
            </div>
          </div>
        </Panel>
      </main>
    </div>
  );
};

export default ChangePassword;
